/*
================================================================================
File: src/components/common/AutocompleteInput.js
Description: A more robust component to prevent browser autofill conflict.
             Waits for Google Maps to load before initializing autocomplete.
================================================================================
*/
import React, { useRef, useEffect, useState } from "react";

const AutocompleteInput = ({ onPlaceSelected, ...props }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [autoCompleteVal] = useState(
    `ac-${Math.random().toString(36).substring(2, 15)}`
  );
  const [mapsReady, setMapsReady] = useState(
    !!(window.google && window.google.maps && window.google.maps.places)
  );
  const [mapsFailed, setMapsFailed] = useState(false);

  // Wait for Google Maps to load
  useEffect(() => {
    if (mapsReady || mapsFailed) return;

    let pollCount = 0;
    const maxPolls = 300; // ~30 seconds of polling at 100ms intervals

    // Retry checking if maps is loaded
    const checkInterval = setInterval(() => {
      pollCount++;
      if (window.google && window.google.maps && window.google.maps.places) {
        setMapsReady(true);
        clearInterval(checkInterval);
      } else if (pollCount >= maxPolls) {
        // Timeout after ~30 seconds - allow manual input instead
        console.warn(
          "Google Maps autocomplete unavailable. Manual address entry still works. " +
          "If this persists, check your API key and internet connection."
        );
        setMapsFailed(true); // Enable fallback mode
        clearInterval(checkInterval);
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [mapsReady, mapsFailed]);

  // Initialize autocomplete once Maps is ready
  useEffect(() => {
    if (!mapsReady || mapsFailed || !inputRef.current) {
      return;
    }

    if (autocompleteRef.current) {
      // Clean up existing autocomplete
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    try {
      // Double-check that Maps is available before trying to initialize
      if (!window.google?.maps?.places?.Autocomplete) {
        console.warn("Google Maps Autocomplete not available, allowing manual entry");
        setMapsFailed(true);
        return;
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["address"],
          componentRestrictions: { country: "us" },
        }
      );

      // Set high z-index for the autocomplete dropdown
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        pacContainer.style.zIndex = '9999';
      }

      const handlePlaceChanged = () => {
        try {
          const place = autocompleteRef.current?.getPlace();
          if (place && place.address_components) {
            onPlaceSelected(place);
          }
        } catch (error) {
          console.error("Error processing place selection:", error);
          // Don't break the form - user can continue with manual entry
        }
      };

      // Ensure pac-container has high z-index when it appears
      const observer = new MutationObserver(() => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          pacContainer.style.zIndex = '9999';
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      autocompleteRef.current.addListener("place_changed", handlePlaceChanged);

      return () => {
        observer.disconnect();
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
        }
      };
    } catch (error) {
      console.error("Failed to initialize autocomplete:", error);
      // Allow manual entry - don't try again
      setMapsFailed(true);
    }
  }, [mapsReady, mapsFailed, onPlaceSelected, inputRef]);

  const handleFocus = (event) => {
    event.target.setAttribute("autoComplete", autoCompleteVal);
  };

  return (
    <div className="flex items-start justify-between p-2 border-b-2 border-gray-200 dark:border-gray-700 last:border-b-0 overflow-visible">
      <label
        className="text-xs font-medium text-gray-500 dark:text-gray-200 uppercase flex-shrink-0 mr-4 pt-2"
        htmlFor="location-search-input"
      >
        Address:
      </label>
      <div className="w-full overflow-visible">
        <input
          ref={inputRef}
          type="text"
          onFocus={handleFocus}
          name="search-location"
          id="location-search-input"
          autoComplete={autoCompleteVal}
          className="w-full text-sm font-medium h-9 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          {...props}
        />
        <p className="text-xs text-gray-500 dark:text-gray-200 mt-1 text-right">
          {mapsFailed
            ? "Autocomplete unavailable. You can still enter your address manually."
            : "For best results, please use the address suggestions. Browser autofill may interfere."}
        </p>
      </div>
    </div>
  );
};

export default AutocompleteInput;
